import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

// Static Terms + Acceptable Use page. Linked from the login + access-request
// flows, and from the bottom of every authed page via BottomNav (not added —
// kept low-friction for beta). Updated 2026-06-15 to add Acceptable Use.
export default function TermsPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen pb-32" data-testid="terms-page">
      <header className="px-5 pt-6 pb-3 max-w-2xl mx-auto w-full">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-timber-700 font-bold"
          data-testid="terms-back"
        >
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        <div className="flex items-center gap-2 mt-3">
          <Logo size={20} />
        </div>
        <h1 className="font-heading text-3xl font-bold mt-3" data-testid="terms-title">
          Terms &amp; Acceptable Use
        </h1>
        <p className="text-sm text-timber-700 mt-1">Last updated: June 2026 — private beta.</p>
      </header>

      <main className="px-5 max-w-2xl mx-auto w-full space-y-6 text-sm text-timber-900 leading-relaxed">
        <Section testid="terms-section-overview" title="What Half Job is">
          <p>
            Half Job is an AI-assisted DIY copilot that helps Australian homeowners plan,
            track, and finish unfinished household projects. It is a planning tool — not
            a substitute for a licensed tradesperson, a professional engineer, or
            common sense on site.
          </p>
        </Section>

        <Section testid="terms-section-acceptable-use" title="Acceptable Use">
          <p>By using Half Job you agree NOT to use it for any of the following:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1.5">
            <li>
              Illegal, dangerous or regulated work — including unlicensed mains-pressure
              plumbing, unlicensed behind-the-wall electrical, gas work, asbestos
              removal beyond a sealed sample, or structural changes without
              engineering sign-off.
            </li>
            <li>
              Building or modifying weapons, explosives, surveillance devices, or
              anything intended to cause harm.
            </li>
            <li>
              Bypassing security systems, breaking and entering, hiding evidence, or
              any other criminal activity.
            </li>
            <li>
              Attempting to jailbreak the AI, extract system prompts, role-reverse,
              or trick the model into producing content unrelated to home DIY.
            </li>
            <li>
              Using Half Job for tasks outside its scope — cooking, schoolwork,
              business strategy, financial advice, medical advice, etc. We can&apos;t
              help with those and won&apos;t pretend to.
            </li>
            <li>Harassment, hate speech, spam, or scraping/automated abuse.</li>
          </ul>
          <p className="mt-3">
            If the AI declines to help with something you&apos;ve typed, that&apos;s the
            guardrail working. We&apos;d rather refuse a plan than ship you one that
            puts you, your family, or your home at risk.
          </p>
        </Section>

        <Section testid="terms-section-scope" title="What Half Job can help with">
          <p>
            Home projects, project planning, materials sourcing, shopping lists,
            troubleshooting, and finishing the projects you&apos;re working on. If
            you&apos;re hoping for something else, please let us know via{" "}
            <span className="font-bold">Future Requests</span> from inside the app.
          </p>
        </Section>

        <Section testid="terms-section-ai-safety" title="AI safety &amp; limits">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              Plans, materials, and advice are AI-generated. They may be wrong,
              incomplete, or out of date. Always double-check before buying
              materials or starting work.
            </li>
            <li>
              When work is outside the legal scope of DIY in Australia (mains
              plumbing past the isolator, behind-the-wall electrical, gas,
              structural, asbestos removal), Half Job will tell you to call a
              licensed trade. Listen to it.
            </li>
            <li>
              Safety warnings are guidance, not a substitute for the manufacturer
              instructions on the products you&apos;re using.
            </li>
          </ul>
        </Section>

        <Section testid="terms-section-beta" title="Private beta">
          <p>
            Half Job is currently invite-only. Admins can approve or revoke access
            at any time. We may delete accounts and their associated data if
            terms are breached — see Account Termination below.
          </p>
        </Section>

        <Section testid="terms-section-data" title="Your data">
          <p>
            We store your account, households, projects, steps, materials and any
            photos you upload. You can request a JSON export or full purge of your
            data by emailing an admin. Photos referenced only by a deleted project
            are removed from storage; photos shared with other projects/users are
            preserved.
          </p>
        </Section>

        <Section testid="terms-section-termination" title="Account termination">
          <p>
            We may suspend or delete your account if you breach Acceptable Use,
            attempt to abuse the AI, or use Half Job to cause harm to yourself or
            others. When an account is purged, the user record, solo households,
            their projects, photos, OTP codes, and waitlist record are removed.
            Households shared with other members are preserved, with ownership
            transferred to the next remaining member.
          </p>
        </Section>

        <Section testid="terms-section-liability" title="Liability">
          <p>
            Half Job is provided &quot;as-is&quot; during private beta. To the
            maximum extent permitted by law, we accept no liability for damage,
            injury, or loss arising from work you carry out using Half Job&apos;s
            guidance. Use it as a planning aid, not as a tradesperson.
          </p>
        </Section>

        <Section testid="terms-section-contact" title="Contact">
          <p>
            Questions, feedback, or data requests? Reach us at{" "}
            <span className="font-mono text-timber-900">hello@halfjob.com.au</span>{" "}
            or via the in-app Future Requests form.
          </p>
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section data-testid={testid} className="tactile-card p-4">
      <h2 className="font-heading text-xl font-bold text-timber-900 mb-2">{title}</h2>
      <div className="space-y-1">{children}</div>
    </section>
  );
}
